import type {LngLat, MapLocationRequest, Margin} from '@yandex-delivery/blocks'
import {head, isEqual} from '@yandex-delivery/utils'
import {useCallback, useMemo, useState} from 'react'

import type {Props} from './types'
import {getMarginWithPlacemarkOffset} from './utils'

export function useFormMap(props: Props, isMobile = false) {
  const {
    location,
    lang,
    onPlacemarkChange,
    mapLocationResult,
    dir = 'ltr',
    isRouteVisible = false,
    isDragPointAllowed = false,
    sourcePointCoordinates,
    orderPointsCoordinates,
    routePointCoordinates,
  } = props
  const [mapLocation, updateMapLocation] = useState<MapLocationRequest>(mapLocationResult)
  const controlsPosition = dir === 'ltr' ? ('right' as const) : ('left' as const)

  const isOnlySourcePoint = useMemo(() => {
    if (sourcePointCoordinates === undefined) {
      return false
    }

    if (orderPointsCoordinates === undefined) {
      return false
    }

    return routePointCoordinates.length === 1 && isEqual(sourcePointCoordinates, head(routePointCoordinates))
  }, [orderPointsCoordinates, routePointCoordinates, sourcePointCoordinates])

  const mapMargin = useMemo<Margin>(() => {
    if (isMobile) {
      return getMarginWithPlacemarkOffset([48, 48, 48, 48])
    }

    // 152 = 64 (высота шапки) + 16(padding формы) + 72(margin карты)
    // 872 = 72(margin карты) + 800(ширина формы)
    const defaultMargin: Margin = dir === 'rtl' ? [152, 872, 88, 88] : [152, 88, 88, 872]

    return getMarginWithPlacemarkOffset(defaultMargin)
  }, [dir, isMobile])

  const geolocationClickHandler = useCallback(
    (coordinates: LngLat) => {
      updateMapLocation({
        center: coordinates,
        duration: 200,
      })

      if (isOnlySourcePoint) {
        onPlacemarkChange(coordinates, 0)
      }
    },
    [isOnlySourcePoint, onPlacemarkChange],
  )

  return {
    controlsPosition,
    isDragPointAllowed,
    isOnlySourcePoint,
    isRouteVisible,
    lang,
    location,
    mapLocation,
    mapMargin,
    geolocationClickHandler,
    routePointCoordinates,
    orderPointsCoordinates,
    onPlacemarkChange,
    updateMapLocation,
  } as const
}
